/**
 * Run Planner Store
 * Manages state for the Smart Run Planner feature
 */

import { create } from 'zustand';
import type {
  RunPreferences,
  GeneratedRoute,
  ScheduledRun,
} from '@/types/runPlanner';
import { useNavigationStore, type RouteStep, type ActiveRoute } from './useNavigationStore';
import { useLocationStore } from './useLocationStore';
import {
  fetchMultipleLoopRoutes,
  extractPolyline,
  type DirectionsRoute,
} from '@/services/routing/mapboxDirections';

interface RunPlannerState {
  // UI State
  isOpen: boolean;
  activeTab: 'plan' | 'routes' | 'scheduled';
  isGenerating: boolean;

  // Planning State
  startPoint: { lat: number; lng: number; address?: string } | null;
  endPoint: { lat: number; lng: number; address?: string } | null;
  targetDistance: number;
  preferences: RunPreferences;
  scheduledTime: { date: string; time: string } | null;
  invitedFriends: string[];

  // Results
  generatedRoutes: GeneratedRoute[];
  selectedRoute: GeneratedRoute | null;

  // Saved runs
  scheduledRuns: ScheduledRun[];
  completedRuns: ScheduledRun[];

  // Actions
  openPlanner: () => void;
  closePlanner: () => void;
  setActiveTab: (tab: 'plan' | 'routes' | 'scheduled') => void;

  setStartPoint: (point: { lat: number; lng: number; address?: string } | null) => void;
  setEndPoint: (point: { lat: number; lng: number; address?: string } | null) => void;
  setTargetDistance: (distance: number) => void;
  setPreferences: (preferences: Partial<RunPreferences>) => void;
  setScheduledTime: (time: { date: string; time: string } | null) => void;
  addInvitedFriend: (friendId: string) => void;
  removeInvitedFriend: (friendId: string) => void;

  generateRoutes: () => Promise<void>;
  selectRoute: (route: GeneratedRoute | null) => void;
  scheduleRun: (route: GeneratedRoute) => void;
  startRun: (runId: string) => void;
  cancelRun: (runId: string) => void;

  reset: () => void;
}

const defaultPreferences: RunPreferences = {
  airQuality: 80,
  minimalCrossings: 70,
  greenSpaces: 60,
  elevation: 'flat',
  loopRoute: true,
  waterStops: false,
  lighting: false,
  shade: false,
};

export const useRunPlannerStore = create<RunPlannerState>((set, get) => ({
  // Initial UI State
  isOpen: false,
  activeTab: 'plan',
  isGenerating: false,

  // Initial Planning State
  startPoint: null,
  endPoint: null,
  targetDistance: 5000, // Default 5K
  preferences: defaultPreferences,
  scheduledTime: null,
  invitedFriends: [],

  // Initial Results
  generatedRoutes: [],
  selectedRoute: null,

  // Saved runs
  scheduledRuns: [],
  completedRuns: [],

  // Actions
  openPlanner: () => set({ isOpen: true }),
  closePlanner: () => set({ isOpen: false }),
  setActiveTab: (tab) => set({ activeTab: tab }),

  setStartPoint: (point) => set({ startPoint: point }),
  setEndPoint: (point) => set({ endPoint: point }),
  setTargetDistance: (distance) => set({ targetDistance: distance }),
  setPreferences: (newPrefs) =>
    set((state) => ({
      preferences: { ...state.preferences, ...newPrefs },
    })),
  setScheduledTime: (time) => set({ scheduledTime: time }),
  addInvitedFriend: (friendId) =>
    set((state) => ({
      invitedFriends: [...state.invitedFriends, friendId],
    })),
  removeInvitedFriend: (friendId) =>
    set((state) => ({
      invitedFriends: state.invitedFriends.filter((id) => id !== friendId),
    })),

  generateRoutes: async () => {
    const currentGPS = useLocationStore.getState().currentPosition;
    const { startPoint, endPoint, targetDistance, preferences } = get();

    // Determine the start coordinates
    // Priority: use explicitly set start point first (from conversation/geocoding),
    // only fall back to GPS if no start point was manually set
    let startCoords: { lat: number; lng: number } | null = null;

    if (startPoint && startPoint.lat !== 0 && startPoint.lng !== 0) {
      // Use manually set start point (from geocoded address in conversation)
      startCoords = { lat: startPoint.lat, lng: startPoint.lng };
      console.log('Using manually set start point:', startCoords);
    } else if (currentGPS) {
      // Fall back to live GPS location
      startCoords = { lat: currentGPS.lat, lng: currentGPS.lng };
      get().setStartPoint({
        lat: currentGPS.lat,
        lng: currentGPS.lng,
        address: 'Current Location',
      });
      console.log('Using GPS location:', startCoords);
    }

    if (!startCoords) {
      console.error('Cannot generate routes: No location available');
      alert('Please enter a starting location or enable GPS to generate routes');
      return;
    }

    console.log('🎯 Generating routes from:', startCoords, 'to:', endPoint || 'loop back to start', 'Distance:', targetDistance, 'm');

    set({ isGenerating: true });

    try {
      let mapboxRoutes: DirectionsRoute[] = [];

      // Check if it's a loop route or point-to-point
      console.log('🛣️ Route mode:', endPoint ? 'has endPoint' : 'no endPoint', 'loopRoute:', preferences.loopRoute);
      if (endPoint && preferences.loopRoute === false) {
        // Point-to-point route - use Mapbox Walking Directions API
        console.log('📍 Using point-to-point walking directions from', startCoords, 'to', endPoint);
        const { fetchWalkingDirections } = await import('@/services/routing/mapboxDirections');
        const routes = await fetchWalkingDirections(
          startCoords,
          { lat: endPoint.lat, lng: endPoint.lng }
        );
        console.log('📍 Mapbox returned', routes?.length || 0, 'routes');
        mapboxRoutes = routes || [];
      } else {
        // Loop route - use loop generation
        const loopRoutes = await fetchMultipleLoopRoutes(
          startCoords,
          targetDistance,
          3 // Get 3 route variations
        );
        // Flatten the array of arrays to a single array
        mapboxRoutes = loopRoutes.flat();
      }

      let generatedRoutes: GeneratedRoute[];

      if (mapboxRoutes.length > 0) {
        // Convert Mapbox routes to our GeneratedRoute format
        console.log(`✅ Generated ${mapboxRoutes.length} real Mapbox routes from: ${startCoords.lat}, ${startCoords.lng}`);
        generatedRoutes = mapboxRoutes.map((route, index) =>
          convertMapboxToGeneratedRoute(route, startCoords, targetDistance, preferences, index)
        );
      } else {
        // Fallback to mock routes if Mapbox fails
        console.warn('⚠️ Mapbox API returned no routes, using mock routes instead from:', startCoords);
        generatedRoutes = generateMockRoutes(startCoords, targetDistance, preferences);
      }

      set({
        generatedRoutes,
        isGenerating: false,
        activeTab: 'routes',
      });
    } catch (error) {
      console.error('❌ Error generating routes:', error);
      // Fallback to mock routes on error
      const mockRoutes = generateMockRoutes(startCoords, targetDistance, preferences);
      set({
        generatedRoutes: mockRoutes,
        isGenerating: false,
        activeTab: 'routes',
      });
    }
  },

  selectRoute: (route) => set({ selectedRoute: route }),

  scheduleRun: (route) => {
    const { scheduledTime, invitedFriends } = get();

    const newRun: ScheduledRun = {
      id: `run_${Date.now()}`,
      route,
      scheduledTime: scheduledTime
        ? new Date(`${scheduledTime.date}T${scheduledTime.time}`)
        : new Date(),
      createdBy: 'current_user',
      participants: invitedFriends.map((id) => ({
        id,
        name: `Friend ${id}`,
        status: 'invited' as const,
      })),
      status: 'scheduled',
    };

    set((state) => ({
      scheduledRuns: [...state.scheduledRuns, newRun],
      activeTab: 'scheduled',
      selectedRoute: null,
    }));
  },

  startRun: (runId) => {
    const { scheduledRuns } = get();
    const run = scheduledRuns.find((r) => r.id === runId);

    if (!run) return;

    // Convert the running route to navigation format
    const route = run.route;
    const startPoint = route.path[0];
    const endPoint = route.path[route.path.length - 1];

    // Generate turn-by-turn steps from route segments
    const steps: RouteStep[] = [];
    let cumulativeDistance = 0;

    // Add start instruction
    steps.push({
      instruction: `Start your ${(route.distance / 1000).toFixed(1)}K run`,
      distance: 0,
      duration: 0,
      maneuver: 'depart',
      coordinates: { lat: startPoint.lat, lng: startPoint.lng },
      roadType: 'park',
      segmentNI: route.scores.airQuality,
    });

    // Add segment instructions
    route.segments.forEach((segment, index) => {
      const segmentStart = route.path[segment.startIndex] || startPoint;
      const instruction = getSegmentInstruction(segment.type, segment.notes);
      const duration = Math.round((segment.distance / 1000) * 6 * 60); // ~6 min/km jogging pace

      steps.push({
        instruction,
        distance: segment.distance,
        duration,
        maneuver: index === 0 ? 'depart' : 'straight',
        coordinates: { lat: segmentStart.lat, lng: segmentStart.lng },
        roadType: segment.type === 'park' || segment.type === 'trail' ? 'park' : 'path',
        segmentNI: segment.airQuality,
      });

      cumulativeDistance += segment.distance;
    });

    // Add finish instruction
    steps.push({
      instruction: 'You have completed your run! Great job!',
      distance: 0,
      duration: 0,
      maneuver: 'arrive',
      coordinates: { lat: endPoint.lat, lng: endPoint.lng },
      roadType: 'park',
      segmentNI: route.scores.airQuality,
    });

    // Create navigation route
    const navigationRoute: ActiveRoute = {
      id: `run_nav_${runId}`,
      priority: 'safest',
      origin: { lat: startPoint.lat, lng: startPoint.lng },
      destination: { lat: endPoint.lat, lng: endPoint.lng },
      destinationName: route.name,
      totalDistance: route.distance,
      totalDuration: route.estimatedDuration.joggingPace * 60,
      steps,
      cognitiveScore: route.scores.overall,
      arrivalNeuralIndex: route.scores.airQuality,
      polyline: route.path.map(p => ({ lat: p.lat, lng: p.lng })),
      averageNI: route.scores.airQuality,
      exposureScore: 100 - route.scores.airQuality,
    };

    // Start navigation
    useNavigationStore.getState().startNavigation(navigationRoute);

    // Update run status and close planner
    set((state) => ({
      scheduledRuns: state.scheduledRuns.map((r) =>
        r.id === runId ? { ...r, status: 'in_progress' as const } : r
      ),
      isOpen: false,
    }));
  },

  cancelRun: (runId) =>
    set((state) => ({
      scheduledRuns: state.scheduledRuns.filter((run) => run.id !== runId),
    })),

  reset: () =>
    set({
      startPoint: null,
      targetDistance: 5000,
      preferences: defaultPreferences,
      scheduledTime: null,
      invitedFriends: [],
      generatedRoutes: [],
      selectedRoute: null,
    }),
}));

// Mock route generator - simulates backend route generation
function generateMockRoutes(
  startPoint: { lat: number; lng: number },
  targetDistance: number,
  _preferences: RunPreferences
): GeneratedRoute[] {
  const distanceKm = targetDistance / 1000;

  // Route A: Best Air Quality (Recommended)
  const routeA: GeneratedRoute = {
    id: `route_${Date.now()}_a`,
    name: `Fresh Air ${distanceKm}K Loop`,
    recommended: true,
    path: generateCircularPath(startPoint, targetDistance),
    distance: targetDistance + Math.random() * 200 - 100,
    estimatedDuration: {
      walkingPace: Math.round(distanceKm * 12),
      joggingPace: Math.round(distanceKm * 6),
      runningPace: Math.round(distanceKm * 5),
    },
    scores: {
      overall: 94,
      airQuality: 96,
      safety: 92,
      greenCoverage: 78,
      neuralIndexImpact: 3.2,
    },
    segments: [
      { startIndex: 0, endIndex: 20, type: 'park', airQuality: 95, distance: targetDistance * 0.3, notes: 'Riverside trail' },
      { startIndex: 20, endIndex: 50, type: 'trail', airQuality: 92, distance: targetDistance * 0.4 },
      { startIndex: 50, endIndex: 80, type: 'waterfront', airQuality: 98, distance: targetDistance * 0.2 },
      { startIndex: 80, endIndex: 100, type: 'residential', airQuality: 88, distance: targetDistance * 0.1 },
    ],
    waypoints: [
      { lat: startPoint.lat, lng: startPoint.lng, type: 'start', name: 'Start/Finish', kmMark: 0 },
      { lat: startPoint.lat + 0.01, lng: startPoint.lng + 0.005, type: 'water_fountain', name: 'Water Station', kmMark: Math.round(distanceKm * 0.4 * 10) / 10 },
      { lat: startPoint.lat + 0.005, lng: startPoint.lng - 0.01, type: 'checkpoint', name: 'Halfway Point', kmMark: Math.round(distanceKm * 0.5 * 10) / 10 },
    ],
    warnings: [],
    forecast: {
      temperature: 24,
      humidity: 65,
      airQualityIndex: 35,
      uvIndex: 4,
      windSpeed: 8,
      recommendation: 'Excellent conditions for running. Stay hydrated.',
    },
  };

  // Route B: Balanced
  const routeB: GeneratedRoute = {
    id: `route_${Date.now()}_b`,
    name: `Urban ${distanceKm}K Route`,
    recommended: false,
    path: generateCircularPath(startPoint, targetDistance, 0.8),
    distance: targetDistance - 50,
    estimatedDuration: {
      walkingPace: Math.round(distanceKm * 12),
      joggingPace: Math.round(distanceKm * 6),
      runningPace: Math.round(distanceKm * 4.8),
    },
    scores: {
      overall: 82,
      airQuality: 78,
      safety: 85,
      greenCoverage: 45,
      neuralIndexImpact: 1.8,
    },
    segments: [
      { startIndex: 0, endIndex: 30, type: 'sidewalk', airQuality: 75, distance: targetDistance * 0.4 },
      { startIndex: 30, endIndex: 60, type: 'park', airQuality: 88, distance: targetDistance * 0.3 },
      { startIndex: 60, endIndex: 100, type: 'residential', airQuality: 72, distance: targetDistance * 0.3 },
    ],
    waypoints: [
      { lat: startPoint.lat, lng: startPoint.lng, type: 'start', name: 'Start/Finish', kmMark: 0 },
      { lat: startPoint.lat + 0.008, lng: startPoint.lng + 0.003, type: 'crossing', name: 'Controlled Crossing', kmMark: Math.round(distanceKm * 0.3 * 10) / 10 },
    ],
    warnings: [
      { type: 'traffic', severity: 'low', message: '2 controlled road crossings on this route' },
    ],
  };

  // Route C: Hill Training (if elevation preference allows)
  const routeC: GeneratedRoute = {
    id: `route_${Date.now()}_c`,
    name: `Hill Training ${distanceKm}K`,
    recommended: false,
    path: generateCircularPath(startPoint, targetDistance, 1.1),
    distance: targetDistance + 300,
    estimatedDuration: {
      walkingPace: Math.round(distanceKm * 14),
      joggingPace: Math.round(distanceKm * 7),
      runningPace: Math.round(distanceKm * 5.5),
    },
    scores: {
      overall: 76,
      airQuality: 88,
      safety: 95,
      greenCoverage: 82,
      neuralIndexImpact: 2.5,
    },
    segments: [
      { startIndex: 0, endIndex: 40, type: 'trail', airQuality: 90, distance: targetDistance * 0.5, notes: 'Uphill section' },
      { startIndex: 40, endIndex: 100, type: 'park', airQuality: 86, distance: targetDistance * 0.5, notes: 'Downhill return' },
    ],
    waypoints: [
      { lat: startPoint.lat, lng: startPoint.lng, type: 'start', name: 'Start/Finish', kmMark: 0 },
      { lat: startPoint.lat + 0.012, lng: startPoint.lng, type: 'landmark', name: 'Summit View', kmMark: Math.round(distanceKm * 0.45 * 10) / 10 },
    ],
    warnings: [
      { type: 'steep', severity: 'medium', message: '+120m elevation gain. Challenging terrain.' },
    ],
  };

  return [routeA, routeB, routeC];
}

/**
 * Convert Mapbox DirectionsRoute to our GeneratedRoute format
 */
function convertMapboxToGeneratedRoute(
  mapboxRoute: DirectionsRoute,
  startPoint: { lat: number; lng: number; address?: string },
  _targetDistance: number,
  _preferences: RunPreferences,
  routeIndex: number
): GeneratedRoute {
  const distanceKm = mapboxRoute.distance / 1000;
  let polyline = extractPolyline(mapboxRoute);

  // Ensure route starts exactly at user's location
  if (polyline.length > 0) {
    const firstPoint = polyline[0];
    const distanceFromStart = Math.sqrt(
      Math.pow((firstPoint.lat - startPoint.lat) * 111000, 2) +
      Math.pow((firstPoint.lng - startPoint.lng) * 111000 * Math.cos(startPoint.lat * Math.PI / 180), 2)
    );

    // If first point is more than 50m from start, prepend the exact start point
    if (distanceFromStart > 50) {
      console.warn(`Route starts ${distanceFromStart.toFixed(0)}m from user location, prepending start point`);
      polyline = [{ lat: startPoint.lat, lng: startPoint.lng }, ...polyline];
    } else {
      // Replace first point with exact start location
      polyline[0] = { lat: startPoint.lat, lng: startPoint.lng };
    }
  }

  // Route names based on index
  const routeNames = [
    `Fresh Air ${distanceKm.toFixed(1)}K Loop`,
    `Scenic ${distanceKm.toFixed(1)}K Route`,
    `Urban ${distanceKm.toFixed(1)}K Path`,
  ];

  // Calculate scores based on route characteristics
  // In production, this would use real air quality data along the route
  const baseAirQuality = 85 + Math.random() * 10;
  const airQualityScore = Math.round(baseAirQuality - routeIndex * 5);
  const safetyScore = Math.round(90 - routeIndex * 3 + Math.random() * 5);
  const greenCoverage = Math.round(70 - routeIndex * 10 + Math.random() * 15);

  // Generate segments from the route legs
  const segments = mapboxRoute.legs.map((leg, legIndex) => {
    const segmentTypes: Array<'park' | 'sidewalk' | 'trail' | 'crossing' | 'residential' | 'waterfront'> =
      ['residential', 'sidewalk', 'park', 'trail'];
    return {
      startIndex: Math.round((legIndex / mapboxRoute.legs.length) * polyline.length),
      endIndex: Math.round(((legIndex + 1) / mapboxRoute.legs.length) * polyline.length),
      type: segmentTypes[legIndex % segmentTypes.length],
      airQuality: airQualityScore - Math.random() * 5,
      distance: leg.distance,
      notes: legIndex === 0 ? 'Starting segment' : undefined,
    };
  });

  // Extract waypoints from legs
  const waypoints = mapboxRoute.legs.slice(0, -1).map((leg, index) => {
    const stepCoords = leg.steps[0]?.maneuver?.location || [startPoint.lng, startPoint.lat];
    return {
      lat: stepCoords[1],
      lng: stepCoords[0],
      type: index === 0 ? 'start' as const : 'checkpoint' as const,
      name: index === 0 ? 'Start/Finish' : `Checkpoint ${index}`,
      kmMark: Math.round((index / mapboxRoute.legs.length) * distanceKm * 10) / 10,
    };
  });

  // Add start waypoint if not present
  if (waypoints.length === 0 || waypoints[0].type !== 'start') {
    waypoints.unshift({
      lat: startPoint.lat,
      lng: startPoint.lng,
      type: 'start' as const,
      name: 'Start/Finish',
      kmMark: 0,
    });
  }

  return {
    id: `route_${Date.now()}_${routeIndex}`,
    name: routeNames[routeIndex] || `Route ${routeIndex + 1}`,
    recommended: routeIndex === 0,
    path: polyline,
    distance: mapboxRoute.distance,
    estimatedDuration: {
      walkingPace: Math.round(mapboxRoute.duration / 60), // Mapbox provides walking time
      joggingPace: Math.round((mapboxRoute.duration / 60) * 0.6), // ~60% of walking time
      runningPace: Math.round((mapboxRoute.duration / 60) * 0.5), // ~50% of walking time
    },
    scores: {
      overall: Math.round((airQualityScore + safetyScore + greenCoverage) / 3),
      airQuality: airQualityScore,
      safety: safetyScore,
      greenCoverage: greenCoverage,
      neuralIndexImpact: Math.round((airQualityScore / 100) * 5 * 10) / 10,
    },
    segments,
    waypoints,
    warnings: routeIndex > 0 ? [
      {
        type: 'traffic' as const,
        severity: 'low' as const,
        message: 'This route may have some road crossings',
      },
    ] : [],
    forecast: {
      temperature: 24 + Math.round(Math.random() * 4),
      humidity: 60 + Math.round(Math.random() * 15),
      airQualityIndex: Math.round(50 - airQualityScore / 2),
      uvIndex: 3 + Math.round(Math.random() * 3),
      windSpeed: 5 + Math.round(Math.random() * 10),
      recommendation: routeIndex === 0
        ? 'Excellent conditions for running. This route has the best air quality.'
        : 'Good conditions. Stay hydrated during your run.',
    },
  };
}

// Get human-readable instruction for a route segment
function getSegmentInstruction(type: string, notes?: string): string {
  const instructions: Record<string, string> = {
    park: 'Continue through the park',
    trail: 'Follow the trail path',
    waterfront: 'Run along the waterfront',
    sidewalk: 'Continue on the sidewalk',
    residential: 'Run through the residential area',
    crossing: 'Cross at the intersection',
  };

  const baseInstruction = instructions[type] || 'Continue on the route';
  return notes ? `${baseInstruction} - ${notes}` : baseInstruction;
}

// Generate a circular path for the route
function generateCircularPath(
  center: { lat: number; lng: number },
  distance: number,
  radiusMultiplier: number = 1
): Array<{ lat: number; lng: number }> {
  const points: Array<{ lat: number; lng: number }> = [];

  // Start at the exact center point (user's location)
  points.push({ lat: center.lat, lng: center.lng });

  const numPoints = 50;

  // Approximate radius based on distance (assuming circular route)
  const circumference = distance;
  const radius = (circumference / (2 * Math.PI)) / 111000; // Convert to degrees
  const adjustedRadius = radius * radiusMultiplier;

  // Generate circular path around the center
  for (let i = 1; i <= numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI;
    // Add some irregularity to make it look natural
    const r = adjustedRadius * (1 + Math.sin(angle * 3) * 0.1);
    points.push({
      lat: center.lat + r * Math.cos(angle),
      lng: center.lng + r * Math.sin(angle) * 1.2, // Adjust for lat/lng ratio
    });
  }

  // Ensure loop closes back at the start point
  points.push({ lat: center.lat, lng: center.lng });

  return points;
}
